import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, RefreshCw, MapPin, MapPinOff, Trash2, Building2, Mic, FileText } from 'lucide-react';
import { gstinVisitsApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';
import RecordingPlayer from '../components/RecordingPlayer';

type Visit = {
  id: number; gstin_id: number; user_name: string; assigned_by: string;
  status: string; scheduled_date: string;
  check_in_time?: string; check_in_lat?: string; check_in_lng?: string;
  check_out_time?: string; check_out_lat?: string; check_out_lng?: string;
  check_out_remark?: string; outcome?: string; remark?: string;
  force_checkin_allowed?: number;
  recording_path?: string;
  email_id?: string;
  tally_serial_no?: string;
  expiry_date?: string;
  partner_name?: string;
  customer_name?: string; person_name?: string; mobile_no?: string;
  address?: string; state?: string; pincode?: string; gstin_no?: string;
  gstin_lat?: string; gstin_lng?: string;
};

// Why the microphone is unavailable, in words a field user can act on.
const micFailureMessage = (e: any): string => {
  if (!navigator.mediaDevices?.getUserMedia) {
    return !window.isSecureContext
      ? 'The microphone needs a secure (https) connection, so this visit cannot be checked in from here.'
      : 'This device blocked microphone access, so this visit cannot be checked in.';
  }
  const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
  return denied
    ? 'Microphone permission is required to check in. Allow the microphone in your device settings and try again.'
    : `Could not start the microphone (${e?.name || 'unknown error'}). Check-in needs the microphone, so try again.`;
};

// The seven fields the GSTIN Database keeps on a party. Captured on the visit
// and written straight back, so both reports read the same record.
type Details = {
  customer_name: string; person_name: string; mobile_no: string;
  email_id: string; tally_serial_no: string; expiry_date: string; partner_name: string;
};
const BLANK_DETAILS: Details = {
  customer_name: '', person_name: '', mobile_no: '',
  email_id: '', tally_serial_no: '', expiry_date: '', partner_name: '',
};
const DETAIL_FIELDS: Array<[keyof Details, string, string]> = [
  ['customer_name',   'Company Name',    'text'],
  ['person_name',     'Person Name',     'text'],
  ['mobile_no',       'Mobile No',       'tel'],
  ['email_id',        'Email ID',        'email'],
  ['tally_serial_no', 'Tally Serial No', 'text'],
  ['expiry_date',     'Expiry Date',     'date'],
  ['partner_name',    'Partner Name',    'text'],
];
// A date arrives as an ISO timestamp but the input needs YYYY-MM-DD.
const toDateInput = (v: any) => (v ? String(v).slice(0, 10) : '');

const OUTCOMES = ['Interested', 'Not Interested', 'Follow Up', 'Demo Given', 'Closed - Won', 'Closed - Lost'];

const fmtDate = (s: any) => { const d = new Date(String(s)); return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
const fmtTime = (s: any) => {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export default function GstinVisits() {
  const { showSuccess, showError } = useToast();
  const { canCheckPermission, isAdmin } = useAuth();
  const admin = isAdmin();

  const [tab, setTab] = useState<'active' | 'completed'>('active');
  const [active, setActive] = useState<Visit[]>([]);
  const [completed, setCompleted] = useState<Visit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Audio is captured for the whole visit: started by check-in, uploaded on
  // check-out. Refs (not state) so a re-render never drops the recorder.
  const [recordingId, setRecordingId] = useState<number | null>(null);
  const [recordingSecs, setRecordingSecs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);

  // Check-out capture
  const [closing, setClosing] = useState<Visit | null>(null);
  const [outcome, setOutcome] = useState('');
  const [remark, setRemark] = useState('');
  const [view, setView] = useState<Visit | null>(null);
  const [details, setDetails] = useState<Details>(BLANK_DETAILS);
  // Set when Update Details is opened on its own rather than inside check-out.
  const [editing, setEditing] = useState<Visit | null>(null);

  const canCreate = canCheckPermission('gstin_visits', 'create');
  const canCheckin = canCheckPermission('gstin_visits', 'checkin');
  const canDelete = canCheckPermission('gstin_visits', 'delete');
  const canResetLoc = canCheckPermission('gstin_visits', 'reset_location');

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'active') {
        const r = await gstinVisitsApi.getActive();
        setActive((r.data || []) as Visit[]);
      } else {
        const r = await gstinVisitsApi.getCompleted({ page, limit: 20, search: debounced || undefined });
        setCompleted((r.data || []) as Visit[]);
        setTotal(r.total || 0);
      }
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load visits');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, debounced]);

  useEffect(() => { load(); }, [load]);

  /** Browser GPS, with the accuracy reading the geofence needs. */
  const getPosition = () => new Promise<{ lat: number; lng: number; accuracy: number }>((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('This device cannot report a location')); return; }
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy || 0 }),
      err => reject(new Error(err.code === 1
        ? 'Location permission denied — allow location access to check in'
        : 'Could not get your location. Move to open sky and try again.')),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });

  /** Open the mic. Throws if unavailable — the caller aborts the check-in. */
  const acquireMic = async (): Promise<MediaStream> => {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error(micFailureMessage(null));
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 8000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e: any) {
      throw new Error(micFailureMessage(e));
    }
  };

  /** Start recording on an already-open stream. */
  const beginRecording = (visitId: number, stream: MediaStream) => {
    streamRef.current = stream;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    // 8kbps opus — telephone quality, roughly 60KB a minute.
    const rec = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 8000 } : { audioBitsPerSecond: 8000 });
    rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.start(1000);
    recorderRef.current = rec;
    setRecordingId(visitId);
    setRecordingSecs(0);
    timerRef.current = setInterval(() => setRecordingSecs(x => x + 1), 1000);
  };
  const stopAndUpload = async (visitId: number) => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecordingSecs(0);
    const rec = recorderRef.current;
    const release = () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      setRecordingId(null);
    };
    if (!rec || rec.state === 'inactive') { release(); return; }
    return new Promise<void>(resolve => {
      rec.onstop = async () => {
        // Let the final ondataavailable land before reading the chunks.
        await new Promise(r => setTimeout(r, 500));
        release();
        const chunks = chunksRef.current;
        chunksRef.current = [];
        if (!chunks.length) { resolve(); return; }
        const mime = chunks[0].type || 'audio/webm';
        const ext = mime.includes('ogg') ? '.ogg' : mime.includes('mp4') ? '.mp4' : '.webm';
        try {
          await gstinVisitsApi.uploadRecording(visitId, new Blob(chunks, { type: mime }), ext);
          showSuccess('Recording saved', 'Audio uploaded');
        } catch (e: any) {
          showError('Recording upload failed', e?.message || 'Unknown error');
        }
        resolve();
      };
      try { rec.requestData(); } catch { /* flush buffered audio */ }
      try { rec.stop(); } catch { release(); resolve(); }
    });
  };

  // Leaving the screen mid-visit must not leave the mic open.
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const doCheckIn = async (v: Visit) => {
    setBusyId(v.id);
    let stream: MediaStream | null = null;
    try {
      // Mic first: a visit that cannot be recorded must not be checked in.
      stream = await acquireMic();
      const pos = await getPosition();
      await gstinVisitsApi.checkIn(v.id, pos);
      showSuccess('Checked in', v.customer_name || `Visit #${v.id}`);
      beginRecording(v.id, stream);
      stream = null;   // handed to the recorder
      load();
    } catch (e: any) {
      showError('Check-in failed', e?.message || 'Could not check in');
    } finally {
      // Released only if the recorder never took it (check-in refused).
      stream?.getTracks().forEach(t => t.stop());
      setBusyId(null);
    }
  };
  const submitCheckOut = async () => {
    if (!closing) return;
    setBusyId(closing.id);
    try {
      // Check-out location is recorded but never blocks: the staff member is
      // already on site, and failing here would strand an open visit.
      let pos: any = {};
      try { pos = await getPosition(); } catch { /* recorded as blank */ }
      // Stop and upload first: the row is about to leave the Active list.
      if (recordingId === closing.id) await stopAndUpload(closing.id);
      await gstinVisitsApi.checkOut(closing.id, { ...pos, remark, outcome, details });
      showSuccess('Checked out', closing.customer_name || `Visit #${closing.id}`);
      setClosing(null); setOutcome(''); setRemark(''); setDetails(BLANK_DETAILS);
      load();
    } catch (e: any) {
      showError('Check-out failed', e?.message || 'Could not check out');
    } finally { setBusyId(null); }
  };

  // Prefill from what the GSTIN record already holds, so staff correct rather
  // than retype — and a field left untouched keeps its existing value.
  const detailsFrom = (v: Visit): Details => ({
    customer_name: v.customer_name || '',
    person_name: v.person_name || '',
    mobile_no: v.mobile_no || '',
    email_id: v.email_id || '',
    tally_serial_no: v.tally_serial_no || '',
    expiry_date: toDateInput(v.expiry_date),
    partner_name: v.partner_name || '',
  });

  const saveDetails = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    try {
      const r: any = await gstinVisitsApi.updateDetails(editing.id, details);
      showSuccess('Details saved', r?.updated ? `${r.updated} field(s) updated in the GSTIN database` : 'Nothing to update');
      setEditing(null); setDetails(BLANK_DETAILS);
      load();
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to save details');
    } finally { setBusyId(null); }
  };

  const doReset = async (v: Visit) => {
    if (!window.confirm(`Reset the saved location for "${v.customer_name || 'this party'}"?\n\nThe stored coordinates will be cleared and the next check-in will save the new location.`)) return;
    setBusyId(v.id);
    try {
      const r: any = await gstinVisitsApi.resetLocation(v.id);
      showSuccess(r?.alreadyEmpty ? 'Nothing to reset' : 'Location reset',
        r?.message || 'Saved location cleared');
      load();
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to reset location');
    } finally { setBusyId(null); }
  };

  const doDelete = async (v: Visit) => {
    if (!window.confirm(`Delete this visit for "${v.customer_name || `#${v.id}`}"?`)) return;
    setBusyId(v.id);
    try {
      await gstinVisitsApi.remove(v.id);
      showSuccess('Deleted', 'Visit removed');
      load();
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to delete');
    } finally { setBusyId(null); }
  };

  // One definition of the per-visit actions, rendered by both the phone cards
  // and the desktop table so the two can never drift apart.
  const actions = (v: Visit, size: 'sm' | 'md' = 'sm') => {
    const icon = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
    const pad = size === 'md' ? 'p-2' : 'p-1';
    const btn = size === 'md' ? 'px-3 py-1.5 text-[13px]' : 'px-2.5 py-1 text-xs';
    return (
      <>
        {canCheckin && !v.check_in_time && (
          <button onClick={e => { e.stopPropagation(); doCheckIn(v); }} disabled={busyId === v.id}
            className={`bg-blue-600 text-white ${btn} rounded font-bold uppercase hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center`}>
            {busyId === v.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'In'}
          </button>
        )}
        {canCheckin && v.check_in_time && !v.check_out_time && (
          <button onClick={e => { e.stopPropagation(); setClosing(v); setOutcome(''); setRemark(''); setDetails(detailsFrom(v)); }}
            className={`bg-emerald-600 text-white ${btn} rounded font-bold uppercase hover:bg-emerald-700 inline-flex items-center gap-1`}>
            {recordingId === v.id && <span className="w-2 h-2 rounded-full bg-red-300 animate-pulse" />}Out
          </button>
        )}
        {admin && (
          <button onClick={async e => {
            e.stopPropagation();
            try {
              await gstinVisitsApi.toggleForceCheckin(v.id, !v.force_checkin_allowed);
              showSuccess('Updated', v.force_checkin_allowed ? 'Force check-in off' : 'Force check-in on');
              load();
            } catch (err: any) { showError('Error', err?.message || 'Failed'); }
          }}
            className={`${pad} rounded border border-transparent transition-all ${
              v.force_checkin_allowed ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`}
            title={v.force_checkin_allowed ? 'Force check-in is ON — geofence bypassed' : 'Allow check-in from anywhere'}>
            <MapPin className={icon} />
          </button>
        )}
        {canResetLoc && (
          <button onClick={e => { e.stopPropagation(); doReset(v); }} disabled={busyId === v.id}
            className={`${pad} text-amber-600 hover:bg-amber-50 rounded border border-transparent hover:border-amber-100 disabled:opacity-50`}
            title="Reset saved location — clears the stored coordinates so the next check-in saves the new ones">
            <MapPinOff className={icon} />
          </button>
        )}
        {canCheckin && (
          <button onClick={e => { e.stopPropagation(); setEditing(v); setDetails(detailsFrom(v)); }}
            className={`${pad} text-slate-600 hover:bg-slate-100 rounded border border-transparent hover:border-slate-200`}
            title="Update the party's details in the GSTIN database">
            <FileText className={icon} />
          </button>
        )}
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); doDelete(v); }} disabled={busyId === v.id}
            className={`${pad} text-red-600 hover:bg-red-50 rounded border border-transparent hover:border-red-100 disabled:opacity-50`}
            title="Delete">
            <Trash2 className={icon} />
          </button>
        )}
      </>
    );
  };

  // One field block, used by check-out and by the standalone form.
  const detailFields = (
    <div className="space-y-2.5">
      {DETAIL_FIELDS.map(([key, label, type]) => (
        <div key={key}>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">{label}</label>
          <input
            type={type}
            value={details[key]}
            onChange={e => setDetails(d => ({ ...d, [key]: e.target.value }))}
            className="w-full border border-slate-300 rounded px-2 py-2 text-[13px] outline-none focus:ring-1 focus:ring-blue-300"
          />
        </div>
      ))}
      <p className="text-[11.5px] text-gray-400 italic">
        Saved onto the GSTIN record. A field left blank keeps its existing value.
      </p>
    </div>
  );

  const th = 'px-3 py-2 text-[11px] font-bold text-gray-700 border-b border-gray-300 bg-slate-100 whitespace-nowrap text-left';
  const td = 'px-3 py-1.5 text-[12.5px] border-b border-gray-200 whitespace-nowrap';
  const rows = tab === 'active' ? active : completed;

  return (
    <div className="p-3 sm:p-4">
      {recordingId && (
        <div className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded bg-red-50 border border-red-200 text-[12.5px] text-red-700">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          Recording this visit · {Math.floor(recordingSecs / 60)}:{String(recordingSecs % 60).padStart(2, '0')}
        </div>
      )}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto">
        <Building2 className="h-5 w-5 text-blue-600 shrink-0" />
        <h1 className="text-lg font-bold text-gray-800 whitespace-nowrap shrink-0">GSTIN Visits</h1>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          {tab === 'completed' && (
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search party / GSTIN / staff…"
                className="pl-8 pr-8 py-1.5 border border-slate-300 rounded text-[13px] w-56 outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
              {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>}
            </div>
          )}
          <button onClick={load} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
        {(['active', 'completed'] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }}
            className={`px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px capitalize ${
              tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t}{t === 'active' && active.length > 0 ? ` (${active.length})` : ''}
          </button>
        ))}
      </div>

      {!canCreate && tab === 'active' && (
        <p className="mb-2 text-[12px] text-gray-400 italic">
          Visits are raised from the GSTIN Database screen.
        </p>
      )}

      {/* Phone: cards. The desktop grid is far too wide for a handset, and
          this is the screen field staff actually use on the road. */}
      <div className="block md:hidden space-y-2.5 pb-24">
        {loading ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm italic">
            {tab === 'active' ? 'No open GSTIN visits' : 'No completed GSTIN visits'}
          </div>
        ) : rows.map(v => (
          <div key={v.id} onClick={() => tab === 'completed' && setView(v)}
            className={`bg-white p-3.5 rounded-xl border-2 border-gray-300 shadow-sm ${tab === 'completed' ? 'cursor-pointer active:bg-gray-50' : ''}`}>
            <div className="flex items-start justify-between gap-2 border-b-2 border-gray-100 pb-2 mb-2">
              <div className="min-w-0">
                <div className="text-[17px] font-bold text-gray-900 truncate">{v.customer_name || '—'}</div>
                <div className="text-[12px] text-gray-400 font-mono truncate">{v.gstin_no || '—'}</div>
              </div>
              <span className="text-[13px] text-gray-500 shrink-0">{fmtDate(v.scheduled_date)}</span>
            </div>

            <div className="flex items-center justify-between gap-2 text-[15px]">
              <span className="text-gray-900 truncate">{v.person_name || v.user_name || '—'}</span>
              {v.mobile_no
                ? <a href={`tel:${v.mobile_no}`} onClick={e => e.stopPropagation()} className="text-blue-600 shrink-0">{v.mobile_no}</a>
                : <span className="text-gray-400 shrink-0">—</span>}
            </div>

            {(v.address || v.state) && (
              <div className="mt-1 text-[13px] text-gray-500 line-clamp-2">
                {[v.address, v.state, v.pincode].filter(Boolean).join(', ')}
              </div>
            )}

            <div className="mt-2 flex items-center gap-3 text-[13px] text-gray-500">
              <span>In {fmtTime(v.check_in_time)}</span>
              <span>Out {fmtTime(v.check_out_time)}</span>
              {!v.gstin_lat && tab === 'active' && (
                <span className="text-amber-600" title="No saved location yet — the first check-in will set it">new</span>
              )}
            </div>

            {tab === 'completed' && (v.outcome || v.check_out_remark) && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                {v.outcome && (
                  <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-bold uppercase bg-emerald-100 text-emerald-700 mr-2">{v.outcome}</span>
                )}
                {v.check_out_remark && <span className="text-[14px] text-gray-700">{v.check_out_remark}</span>}
              </div>
            )}

            {tab === 'active' && (
              <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center gap-2">
                {actions(v, 'md')}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 230px)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={th}>Party</th>
              <th className={th}>GSTIN</th>
              <th className={th}>Contact</th>
              <th className={th}>Staff</th>
              <th className={`${th} text-center`}>Date</th>
              <th className={`${th} text-center`}>In</th>
              <th className={`${th} text-center`}>Out</th>
              {tab === 'completed' && <th className={th}>Outcome</th>}
              {tab === 'completed' && <th className={th}>Remark</th>}
              {tab === 'active' && <th className={`${th} text-center`}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="py-12 text-center text-gray-400 text-sm">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="py-12 text-center text-gray-400 text-sm italic">
                {tab === 'active' ? 'No open GSTIN visits' : 'No completed GSTIN visits'}
              </td></tr>
            ) : rows.map(v => (
              <tr key={v.id} className="hover:bg-blue-50/40">
                <td className={`${td} font-semibold text-gray-900 max-w-[240px] truncate`} title={v.customer_name || ''}>
                  {v.customer_name || '—'}
                  {!v.gstin_lat && <span className="ml-1.5 text-[10px] font-normal text-amber-600" title="No saved location yet — the first check-in will set it">new</span>}
                </td>
                <td className={`${td} text-gray-500 font-mono text-[11.5px]`}>{v.gstin_no || '—'}</td>
                <td className={`${td} text-gray-700`}>
                  {v.mobile_no ? <a href={`tel:${v.mobile_no}`} className="text-blue-600 hover:underline">{v.mobile_no}</a> : '—'}
                  {v.person_name && <span className="text-gray-400"> · {v.person_name}</span>}
                </td>
                <td className={`${td} text-gray-600`}>{v.user_name || '—'}</td>
                <td className={`${td} text-center text-gray-500`}>{fmtDate(v.scheduled_date)}</td>
                <td className={`${td} text-center text-gray-500`}>{fmtTime(v.check_in_time)}</td>
                <td className={`${td} text-center text-gray-500`}>{fmtTime(v.check_out_time)}</td>
                {tab === 'completed' && (
                  <td className={`${td}`}>
                    {v.outcome
                      ? <span className="px-1.5 py-0.5 rounded text-[10.5px] font-bold uppercase bg-emerald-100 text-emerald-700">{v.outcome}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                )}
                {tab === 'completed' && (
                  <td className={`${td} max-w-[260px] truncate text-gray-600 cursor-pointer hover:text-blue-700`}
                    title={v.check_out_remark || ''} onClick={() => setView(v)}>
                    {v.recording_path && <Mic className="inline h-3.5 w-3.5 text-red-500 mr-1" aria-label="Has a recording" />}
                    {v.check_out_remark || '—'}
                  </td>
                )}
                {tab === 'active' && (
                  <td className={`${td} text-center`}>
                    <div className="flex items-center justify-center gap-1">
                      {actions(v)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tab === 'completed' && total > 20 && (
        <div className="flex items-center justify-between mt-2 mb-20 md:mb-0 text-[12px] text-gray-500">
          <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
          <div className="flex gap-1">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-2.5 py-1 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">Prev</button>
            <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}
              className="px-2.5 py-1 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">Next</button>
          </div>
        </div>
      )}

      {/* Check-out */}
      {closing && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setClosing(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-4 py-3 border-b">
              <div>
                <h2 className="text-[15px] font-bold text-gray-900">Check Out</h2>
                <p className="text-[12.5px] text-gray-500">{closing.customer_name}</p>
              </div>
              <button onClick={() => setClosing(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Outcome</label>
                <select value={outcome} onChange={e => setOutcome(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-[13px] bg-white outline-none focus:ring-1 focus:ring-blue-300">
                  <option value="">Select…</option>
                  {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Remark</label>
                <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={4}
                  placeholder="What happened on this visit…"
                  className="w-full border border-slate-300 rounded px-2 py-2 text-[13px] outline-none focus:ring-1 focus:ring-blue-300" />
              </div>
              <div className="pt-1 border-t">
                <p className="text-[11px] font-bold text-gray-500 uppercase mb-2 mt-2">Party Details</p>
                {detailFields}
              </div>
            </div>
            <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setClosing(null)} className="px-3 py-1.5 text-[13px] border border-slate-300 rounded hover:bg-white">Cancel</button>
              <button onClick={submitCheckOut} disabled={busyId === closing.id}
                className="px-4 py-1.5 text-[13px] font-semibold text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-60">
                {busyId === closing.id ? 'Saving…' : 'Check Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Details on its own */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-4 py-3 border-b">
              <div>
                <h2 className="text-[15px] font-bold text-gray-900">Update Details</h2>
                <p className="text-[12.5px] text-gray-500">{editing.customer_name}</p>
              </div>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-4 overflow-auto flex-1">{detailFields}</div>
            <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-[13px] border border-slate-300 rounded hover:bg-white">Cancel</button>
              <button onClick={saveDetails} disabled={busyId === editing.id}
                className="px-4 py-1.5 text-[13px] font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60">
                {busyId === editing.id ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completed detail */}
      {view && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-4 py-3 border-b">
              <div>
                <h2 className="text-[15px] font-bold text-gray-900">{view.customer_name}</h2>
                <p className="text-[12.5px] text-gray-500">{view.gstin_no} · {fmtDate(view.scheduled_date)}</p>
              </div>
              <button onClick={() => setView(null)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2 text-[13px]">
              {([
                ['Visited by', view.user_name],
                ['Contact', [view.person_name, view.mobile_no].filter(Boolean).join(' · ')],
                ['Address', [view.address, view.state, view.pincode].filter(Boolean).join(', ')],
                ['In / Out', `${fmtTime(view.check_in_time)} → ${fmtTime(view.check_out_time)}`],
                ['Outcome', view.outcome],
                ['Remark', view.check_out_remark],
              ] as [string, any][]).map(([k, val]) => (
                <div key={k} className="flex gap-3">
                  <span className="w-24 shrink-0 text-gray-500">{k}</span>
                  <span className="text-gray-900 flex-1 break-words">{val || '—'}</span>
                </div>
              ))}
              {view.recording_path && (
                <div className="flex gap-3 pt-2 border-t">
                  <span className="w-24 shrink-0 text-gray-500">Recording</span>
                  <RecordingPlayer src={gstinVisitsApi.getRecordingUrl(view.id)} className="flex-1 h-9" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
